import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2, Square } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const VoiceInput = ({ onTranscription, disabled = false, className = '' }) => {
  const { api } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Check microphone permission on mount
  useEffect(() => {
    checkMicrophonePermission();
    return () => {
      stopRecording();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const checkMicrophonePermission = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'microphone' });
      setHasPermission(result.state === 'granted');
      result.onchange = () => setHasPermission(result.state === 'granted');
    } catch (e) {
      // Permissions API not supported, will check on first recording
      setHasPermission(null);
    }
  };

  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      
      streamRef.current = stream;
      setHasPermission(true);
      
      // Create MediaRecorder with supported format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = handleRecordingStop;
      
      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setHasPermission(false);
      
      if (error.name === 'NotAllowedError') {
        toast.error('Permiso de micrófono denegado. Por favor, habilítalo en la configuración del navegador.');
      } else {
        toast.error('Error al acceder al micrófono');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsRecording(false);
  };

  const handleRecordingStop = async () => {
    setIsProcessing(true);
    
    try {
      // Create blob from chunks
      const audioBlob = new Blob(chunksRef.current, { 
        type: mediaRecorderRef.current?.mimeType || 'audio/webm' 
      });
      
      // Check minimum size (avoid empty recordings)
      if (audioBlob.size < 1000) {
        toast.error('Grabación muy corta. Intenta hablar más tiempo.');
        setIsProcessing(false);
        return;
      }
      
      // Create form data
      const formData = new FormData();
      const extension = audioBlob.type.includes('webm') ? 'webm' : 'mp4';
      formData.append('audio', audioBlob, `recording.${extension}`);
      formData.append('language', 'es');
      
      // Send to server for transcription
      const response = await api.post('/voice/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.text) {
        onTranscription(response.data.text);
        toast.success('Transcripción completada');
      } else {
        toast.error('No se detectó voz en la grabación');
      }
      
    } catch (error) {
      console.error('Error transcribing:', error);
      toast.error('Error al transcribir el audio');
    } finally {
      setIsProcessing(false);
      chunksRef.current = [];
    }
  };

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Disabled or processing state
  if (disabled || isProcessing) {
    return (
      <button
        disabled
        className={`p-2 rounded-lg bg-slate-700/50 text-slate-500 cursor-not-allowed ${className}`}
        title={isProcessing ? 'Procesando...' : 'Voz deshabilitada'}
      >
        {isProcessing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <MicOff className="w-5 h-5" />
        )}
      </button>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleClick}
        data-testid="voice-input-btn"
        className={`p-2 rounded-lg transition-all ${
          isRecording 
            ? 'bg-red-500 text-white animate-pulse' 
            : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'
        }`}
        title={isRecording ? 'Detener grabación' : 'Grabar voz'}
      >
        {isRecording ? (
          <Square className="w-5 h-5" />
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>
      
      {/* Recording indicator */}
      {isRecording && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
          <span className="inline-block w-2 h-2 bg-white rounded-full mr-1 animate-pulse" />
          {formatTime(recordingTime)}
        </div>
      )}
    </div>
  );
};

export default VoiceInput;
